import React from "react";
import moment from "moment";
import {
  InfoGrid,
  InfoRow,
  Key,
  Value,
  Title,
  Section,
  CenterRow,
  InfoRowPlan,
  KeyValue,
  Wrapper,
  SubTitle,
} from "./style";
import { IServiceProviderProfile } from "../../../../../../interfaces/entities/IServiceProviderProfile";
import { IProviderPhoneNumber } from "../../../../../../interfaces/entities/IProviderPhoneNumber";
import constants from "../../../../../../consts/conts";
import { Carousel } from "../../../../../../components/Carousel/Carousel";
import { AudioPlayer } from "../../../../../../components/AudioPlayer/AudioPlayer";
import { CoverImage } from "../../../../../../components/CoverImage/CoverImage";
import { ProfilePhoto } from "../../../../../../components/ProfilePhoto/index";

type FormattedService = {
  service_id: string;
  service_name: string;
  service_description: string;
  service_price: string | number;
};

type PlanGroup = {
  plan_id: number;
  plan_name: string;
  services: FormattedService[];
};

type FormattedResult = {
  [planId: number]: PlanGroup;
};

export const ProviderTable: React.FC<{
  profile: IServiceProviderProfile | null;
}> = ({ profile }) => {
  const get_payment_status = () => {
    if (!profile) {
      return;
    }

    switch (profile?.id_payment_status_srcprf) {
      case constants.payment_status.free:
        return "Grátis";

      case constants.payment_status.payment_ok:
        return "Pagamento OK";

      case constants.payment_status.payment_late:
        return "Pagamento em atraso";
    }
  };

  const format_service_provided = (): FormattedResult => {
    if (
      !profile ||
      !profile.provider_services_provided ||
      !profile.provider_services_provided.length
    ) {
      return [];
    }

    const result = profile.provider_services_provided.reduce(
      (carry: FormattedResult, service_provided) => {
        if (!service_provided?.services_provided?.plan?.id_pln) {
          return carry;
        }

        const plan = service_provided.services_provided.plan;

        if (!carry[plan.id_pln]) {
          carry[plan.id_pln] = {
            plan_id: plan.id_pln,
            plan_name: plan.name_pln,
            services: [],
          };
        }

        carry[plan.id_pln].services.push({
          service_id: service_provided.id_prvsrcprd,
          service_name: service_provided.services_provided.name_srvprd,
          service_description:
            service_provided.services_provided.description_srvprd,
          service_price: service_provided.price_prvsrcprd ?? "",
        });

        return carry;
      },
      []
    );
    return result;
  };

  const render_music_page_schedule = () => {
    if (
      !profile ||
      profile.id_page_type_srcprf !==
        constants.service_provider_profile.music_page
    ) {
      return;
    }

    const music_page_schedule = profile.music_page?.music_page_schedule;

    const dayNames = [
      "Domingo",
      "Segunda-feira",
      "Terça-feira",
      "Quarta-feira",
      "Quinta-feira",
      "Sexta-feira",
      "Sábado",
    ];

    const days_of_week = dayNames.map((day_name, day_number) => {
      const schedules =
        music_page_schedule?.filter(
          (schedule) => schedule.day_of_week_mspgsch === day_number
        ) || [];

      const is_blocked =
        schedules.length === 0 ||
        schedules.every((s) => s.blocked_mspgsch === true);

      return {
        day_number,
        day_name,
        schedules,
        is_blocked,
      };
    });

    return (
      <Section>
        <Title>Dias disponíveis cadastrados</Title>
        <hr />

        {days_of_week.map((day_of_week) => {
          if (!day_of_week.schedules.length) return null;

          return (
            <Section key={day_of_week.day_number}>
              <KeyValue>
                <Key>{day_of_week.day_name}</Key>
                <Value>
                  - {day_of_week.is_blocked ? "Bloqueado" : "Disponivel"}
                </Value>
              </KeyValue>

              {day_of_week.schedules.map((schedule) => (
                <Wrapper key={schedule.id_mspgsch}>
                  <p>
                    {` • Disponível das ${moment
                      .utc(schedule.start_time_mspgsch)
                      .format("HH:mm")} às ${moment
                      .utc(schedule.end_time_mspgsch)
                      .format("HH:mm")}`}
                  </p>
                </Wrapper>
              ))}
            </Section>
          );
        })}
      </Section>
    );
  };

  const render_closed_schedule = () => {
    if (
      !profile ||
      profile.id_page_type_srcprf !==
        constants.service_provider_profile.music_page
    ) {
      return;
    }

    const schedule_closed = profile.music_page?.music_page_schedule_closed;
    if (!schedule_closed?.length) {
      return null;
    }

    return (
      <Section>
        <Title>Horários Indisponíveis</Title>
        <hr />

        {schedule_closed.map((closed) => {
          return (
            <InfoRowPlan key={closed.id_mspgschcl}>
              <KeyValue>
                <Key>Data:</Key>
                <Value>
                  {moment.utc(closed.date_event_mspgschcl).format("DD/MM/YYYY")}
                </Value>
              </KeyValue>
              <KeyValue>
                <Key>Horario:</Key>
                <Value>
                  {` • Disponível das ${moment
                    .utc(closed.start_time_mspgschcl)
                    .format("HH:mm")} às ${moment
                    .utc(closed.end_time_mspgschcl)
                    .format("HH:mm")}`}
                </Value>
              </KeyValue>
            </InfoRowPlan>
          );
        })}
      </Section>
    );
  };

  const renderMusicPage = () => {
    if (
      profile?.id_page_type_srcprf !==
      constants.service_provider_profile.music_page
    ) {
      return null;
    }

    return (
      <>
        <Title>Músico</Title>
        <hr />
        <InfoGrid>
          <InfoRow>
            <Key>Musico Page ID :</Key>
            <Value>{profile?.music_page?.id_mscpg}</Value>
          </InfoRow>
          <InfoRow>
            <Key>Regiões:</Key>
            {profile?.music_page?.music_page_localities &&
              profile?.music_page?.music_page_localities?.length > 0 && (
                <Value>
                  {profile?.music_page?.music_page_localities
                    .map((locality) => locality.cities?.formatted_name_city)
                    .join(", ")}
                </Value>
              )}
          </InfoRow>

          <InfoRow>
            <Key>Regiões Secundárias:</Key>
            {profile?.music_page?.music_page_secondary_localities &&
              profile?.music_page?.music_page_secondary_localities?.length >
                0 && (
                <Value>
                  {profile?.music_page?.music_page_secondary_localities
                    .map((locality) => locality.cities?.formatted_name_city)
                    .join(", ")}
                </Value>
              )}
          </InfoRow>
        </InfoGrid>
        {render_music_page_schedule()}
        {render_closed_schedule()}
        {render_music_page_playlist()}
        <Section>
          <Title>Foto de Capa</Title>
          <hr />
          {profile?.music_page?.cover_image_path_mscpg && (
            <CoverImage
              image_path={profile?.music_page?.cover_image_path_mscpg}
              impage_id={profile?.music_page?.cover_image_mscpg}
              link={profile?.music_page?.cover_image_path_mscpg}
            />
          )}
        </Section>
        <Section>
          <Title>Fotos</Title>
          <hr />
          <Carousel
            medias={
              profile?.music_page?.music_page_image?.map((image) => ({
                media_id: image.id_mscpgimg,
                media_path: image.path_mscpgimg,
                link: image.path_mscpgimg,
              })) || []
            }
          />
        </Section>
        <Section>
          <Title>Videos</Title>
          <hr />
          <Carousel
            medias={
              profile?.music_page?.music_page_videos?.map((videos) => ({
                media_id: videos.id_mscpgvid,
                media_path: videos.thumb_mscpgvid,
                link: videos.path_mscpgvid,
              })) || []
            }
          />
        </Section>
        <Section>
          <Title>Audio</Title>
          <hr />
          <CenterRow>
            {profile?.music_page?.music_page_audios &&
              profile?.music_page?.music_page_audios?.length > 0 && (
                <AudioPlayer
                  src={profile?.music_page?.music_page_audios[0].path_mscpgaud}
                  title={
                    profile?.music_page?.music_page_audios[0]
                      .audio_name_mscpgaud
                  }
                />
              )}
          </CenterRow>
        </Section>
      </>
    );
  };

  const renderFreelancerPage = () => {
    if (
      profile?.id_page_type_srcprf !==
      constants.service_provider_profile.freelancer_page
    ) {
      return null;
    }
    return (
      <>
        <Title>FreeLancer</Title>
        <hr />
      </>
    );
  };

  const renderServicePage = () => {
    if (
      profile?.id_page_type_srcprf !==
      constants.service_provider_profile.service_provider
    ) {
      return null;
    }
    return (
      <>
        <Title>Prestador de Serviço</Title>
        <hr />
      </>
    );
  };

  const get_boost_plan = (boost_plan: number) => {
    switch (boost_plan) {
      case constants.boost_plan.free:
        return "Grátis";
      case constants.boost_plan.plus:
        return "Plus";
      case constants.boost_plan.premium:
        return "Premium";
    }
  };

  const get_page_type = (page_type: number) => {
    switch (page_type) {
      case constants.service_provider_profile.music_page:
        return "Músico";
      case constants.service_provider_profile.freelancer_page:
        return "FreeLancer";
      case constants.service_provider_profile.service_provider:
        return "Prestador de Serviço";
    }
  };

  const render_profile = () => {
    if (!profile) {
      return null;
    }

    const phone_number =
      profile?.provider_phone_number && profile.provider_phone_number.length > 0
        ? profile.provider_phone_number[0]
        : ({} as IProviderPhoneNumber);

    return (
      <>
        <Title>Perfil</Title>
        <hr />
        <ProfilePhoto
          image_path={profile.photo_srcprf}
          link={profile.photo_srcprf}
        />

        <InfoGrid>
          <InfoRow>
            <Key> Show Music ID : </Key>
            <Value>{profile.id_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Email:</Key>
            <Value>{profile.email_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Nome de apresentação:</Key>
            <Value>{profile.apresentation_name}</Value>
          </InfoRow>
          <InfoRow>
            <Key>@:</Key>
            <Value>{profile.nickname_srcprf}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Tipo Perfil:</Key>
            <Value>
              {profile.id_service_provider_type_srcprf === 1 ? "CPF" : "CNPJ"}
            </Value>
          </InfoRow>
          <InfoRow>
            <Key> Tipo Pagina:</Key>
            <Value>{profile.id_page_type_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Tipo Perfil:</Key>
            <Value>{get_page_type(profile.id_page_type_srcprf)}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Verificado:</Key>
            <Value>{profile.verified_srcprf ? "Sim" : "Não"}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Likes:</Key>
            <Value>{profile.likes_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Dislikes:</Key>
            <Value>{profile.dislike_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Deletado:</Key>
            <Value>{profile.deleted_srcprf ? "Sim" : "Não"}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Plano Contratado:</Key>
            <Value>{get_boost_plan(profile.id_boost_plan_srcprf)}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Total Show:</Key>
            <Value>{profile.total_show_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Possui Equipamento:</Key>
            <Value>{profile.has_equipament ? "Sim" : "Não"}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Gerente de Contas:</Key>
            <Value>{profile.agent_id_srcprf}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Celular:</Key>
            <Value>{phone_number.formated_number_prvphnbr}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Data de Criação:</Key>
            <Value>
              {moment.utc(profile.created_at_srcprf).format("DD/MM/YYYY")}
            </Value>
          </InfoRow>
          <InfoRow>
            <Key> Data de Ultima Atualizacao:</Key>
            <Value>
              {moment.utc(profile.updated_at_srcprf).format("DD/MM/YYYY")}
            </Value>
          </InfoRow>
        </InfoGrid>
      </>
    );
  };

  const render_legal_person = () => {
    if (!profile?.provider_legal) {
      return null;
    }
    return (
      <>
        <Title>Pessoa Juridica</Title>
        <hr />
        <InfoGrid>
          <InfoRow>
            <Key>Nome fantasia :</Key>
            <Value>{profile.provider_legal?.name_prvlgl}</Value>
          </InfoRow>

          <InfoRow>
            <Key>Razao Social :</Key>
            <Value>{profile.provider_legal?.registered_name_prvlgl}</Value>
          </InfoRow>

          <InfoRow>
            <Key>Documento :</Key>
            <Value>{profile.provider_legal?.cnpj_prvlgl}</Value>
          </InfoRow>
        </InfoGrid>
      </>
    );
  };

  const render_person = () => {
    if (!profile?.provider_pearson) {
      return null;
    }
    return (
      <>
        <Title>Pessoa Fisíca</Title>
        <hr />
        <InfoGrid>
          <InfoRow>
            <Key>NOME :</Key>
            <Value>{profile.provider_pearson?.name_prvprs}</Value>
          </InfoRow>

          <InfoRow>
            <Key>Documento :</Key>
            <Value>{profile.provider_pearson?.cpf_prvprs}</Value>
          </InfoRow>
          <InfoRow>
            <Key>Data Nascimento :</Key>
            <Value>
              {moment
                .utc(profile.provider_pearson?.birthday_prvprs)
                .format("DD/MM/YYYY")}
            </Value>
          </InfoRow>

          <InfoRow>
            <Key>Genero :</Key>
            <Value>{profile.provider_pearson?.gender_prvprs}</Value>
          </InfoRow>
        </InfoGrid>
      </>
    );
  };

  const render_integration = () => {
    if (!profile?.provider_pearson) {
      return null;
    }
    return (
      <>
        <Title>Integrações</Title>
        <hr />
        <Wrapper>
          <SubTitle>Pagar Me</SubTitle>
          <hr />
          <InfoGrid>
            <InfoRow>
              <Key> Pagar Me ID:</Key>
              <Value>{profile.pagarme_id_srcprf}</Value>
            </InfoRow>
            <InfoRow>
              <Key> Pagar Me Status:</Key>
              <Value>{profile.pagarme_status_srcprf}</Value>
            </InfoRow>
          </InfoGrid>
        </Wrapper>
        <Wrapper>
          <SubTitle>FaturaAgil</SubTitle>
          <hr />
          <InfoGrid>
            <InfoRow>
              <Key> Fatura Agil ID:</Key>
              <Value>{profile.integration_id_srcprf}</Value>
            </InfoRow>
            <InfoRow>
              <Key> Fatura Agil Status Pagamento:</Key>
              <Value>{get_payment_status()}</Value>
            </InfoRow>
          </InfoGrid>
        </Wrapper>
      </>
    );
  };

  const render_finantial = () => {
    if (!profile?.provider_pearson) {
      return null;
    }
    return (
      <>
        <Title>Financeiro</Title>
        <hr />
        <InfoGrid>
          <InfoRow>
            <Key> Pagamento Cadastrado:</Key>
            <Value>
              {profile.payment_type_srcprf === constants.payment.type_boleto_pix
                ? "PIX"
                : "Cartão de Crédito"}
            </Value>
          </InfoRow>
          <InfoRow>
            <Key> Numero do cartão:</Key>
            <Value>{profile.card_number_srcprf}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Nome no cartão:</Key>
            <Value>{profile.card_name_srcprf}</Value>
          </InfoRow>
        </InfoGrid>

        <Wrapper>
          <SubTitle>Planos Contratados</SubTitle>
          <hr />
          {profile.provider_services_provided &&
            profile.provider_services_provided?.length > 0 &&
            Object.values(format_service_provided()).map((service_provided) => {
              return (
                <Section key={service_provided.plan_id}>
                  <KeyValue>
                    <Key> Plano: </Key>
                    <Value>{service_provided.plan_name}</Value>
                  </KeyValue>
                  {service_provided.services.map((service) => {
                    return (
                      <InfoRowPlan key={service.service_id}>
                        •
                        <KeyValue>
                          <Key> Tipo: </Key>
                          <Value>{service.service_name}</Value>
                        </KeyValue>
                        <KeyValue>
                          <Key> Descrição: </Key>
                          <Value>{service.service_description}</Value>
                        </KeyValue>
                        <KeyValue>
                          <Key> Valor Cobrado: </Key>
                          <Value>
                            {new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(Number(service.service_price))}
                          </Value>
                        </KeyValue>
                      </InfoRowPlan>
                    );
                  })}
                </Section>
              );
            })}
        </Wrapper>
      </>
    );
  };

  const render_provider_addres = () => {
    if (!profile?.provier_address) {
      return null;
    }

    const addres = profile.provier_address;

    return (
      <>
        <Title>Endereço:</Title>
        <hr />
        <InfoGrid>
          <InfoRow>
            <Key> Cidade:</Key>
            <Value>{`${addres.cidade_adrs}-${addres.uf_adrs}`}</Value>
          </InfoRow>
          <InfoRow>
            <Key> Bairro:</Key>
            <Value>{addres.bairro_adrs}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Rua:</Key>
            <Value>{addres.rua_adrs}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Número:</Key>
            <Value>{addres.numero_adrs}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Complemento:</Key>
            <Value>{addres.complemento_adrs}</Value>
          </InfoRow>

          <InfoRow>
            <Key> CEP:</Key>
            <Value>{addres.cep_adrs}</Value>
          </InfoRow>

          <InfoRow>
            <Key> Endereço Formatado:</Key>
            <Value>{addres.formatted_adrs}</Value>
          </InfoRow>
        </InfoGrid>
      </>
    );
  };

  const render_music_page_playlist = () => {
    if (
      profile?.id_page_type_srcprf !==
      constants.service_provider_profile.music_page
    ) {
      return null;
    }

    const playlist = profile.music_page?.music_page_play_list;

    if (!playlist || !playlist.length) {
      return null;
    }

    return (
      <Section>
        <Title>Playlist</Title>
        <hr />

        {playlist.map((music) => {
          return (
            <InfoRowPlan key={music.id_mscpgplylst}>
              <KeyValue>
                <Key>{`${music.song_writer_mscpgplylst}`}</Key>
                <Value>{` ${music.name_mscpgplylst}`}</Value>
              </KeyValue>
            </InfoRowPlan>
          );
        })}
      </Section>
    );
  };

  return (
    <>
      {render_profile()}
      {render_person()}
      {render_legal_person()}
      {render_provider_addres()}
      {render_integration()}
      {render_finantial()}
      {renderMusicPage()}
      {renderFreelancerPage()}
      {renderServicePage()}
    </>
  );
};
